/**
 * WB4 Depth Layer 2 — Inspector signal field contract (API DTO ↔ resolver SSOT).
 */

import type { DomainCockpitPilotKey } from './workbench-domain-cockpit-invariants';
import { resolveDomainCockpitPilotKey } from './workbench-domain-cockpit-invariants';
import type { UniversalBaseEntity } from '@shared/types/entity';

export interface InspectorSignalFieldSpec {
  signalId: string;
  label: string;
  objectFieldKeys: readonly string[];
  emptyDisplayDefault: string;
}

/** Pilot → signal field contract (object keys that feed resolveDomainInspectorSignals). */
export const INSPECTOR_SIGNAL_FIELD_CONTRACT: Readonly<
  Record<DomainCockpitPilotKey, readonly InspectorSignalFieldSpec[]>
> = {
  entity_customer: [
    {
      signalId: 'ar_aging',
      label: 'AR 연체',
      objectFieldKeys: ['arAgingDays', 'ar_aging_days'],
      emptyDisplayDefault: '0일',
    },
    {
      signalId: 'credit_hold',
      label: '신용 보류',
      objectFieldKeys: ['creditHold', 'credit_hold'],
      emptyDisplayDefault: '해제',
    },
    {
      signalId: 'relation_count',
      label: '연결 관계',
      objectFieldKeys: ['relationCount', 'relation_count'],
      emptyDisplayDefault: '0',
    },
    {
      signalId: 'linked_documents',
      label: '연결 문서',
      objectFieldKeys: ['linkedDocuments', 'linked_documents'],
      emptyDisplayDefault: '없음',
    },
  ],
  finance: [
    {
      signalId: 'overdue_amount',
      label: '연체 금액',
      objectFieldKeys: ['overdueAmount', 'overdue_amount'],
      emptyDisplayDefault: '0',
    },
    {
      signalId: 'voucher_status',
      label: '전표 상태',
      objectFieldKeys: ['voucherStatus', 'voucher_status'],
      emptyDisplayDefault: '미지정',
    },
    {
      signalId: 'approval_flags',
      label: '승인 플래그',
      objectFieldKeys: ['approvalFlags', 'approval_flags'],
      emptyDisplayDefault: '없음',
    },
    {
      signalId: 'credit_limit',
      label: '여신 한도',
      objectFieldKeys: ['creditLimit', 'credit_limit'],
      emptyDisplayDefault: '미설정',
    },
    {
      signalId: 'payment_behavior',
      label: '결제 성향',
      objectFieldKeys: ['paymentBehavior', 'payment_behavior'],
      emptyDisplayDefault: '미분류',
    },
  ],
  finance_collection: [
    {
      signalId: 'ar_aging',
      label: 'AR 연체',
      objectFieldKeys: ['arAgingDays', 'ar_aging_days'],
      emptyDisplayDefault: '0일',
    },
    {
      signalId: 'credit_hold',
      label: '신용 보류',
      objectFieldKeys: ['creditHold', 'credit_hold'],
      emptyDisplayDefault: '해제',
    },
    {
      signalId: 'overdue_amount',
      label: '연체 금액',
      objectFieldKeys: ['overdueAmount', 'overdue_amount'],
      emptyDisplayDefault: '0',
    },
    {
      signalId: 'payment_behavior',
      label: '결제 성향',
      objectFieldKeys: ['paymentBehavior', 'payment_behavior'],
      emptyDisplayDefault: '미분류',
    },
  ],
  warehouse_picking: [
    {
      signalId: 'picking_status',
      label: '피킹 상태',
      objectFieldKeys: ['pickingStatus', 'picking_status', 'status'],
      emptyDisplayDefault: '미지정',
    },
    {
      signalId: 'dispatch_queue',
      label: '출고 대기열',
      objectFieldKeys: ['dispatchQueue', 'dispatch_queue'],
      emptyDisplayDefault: '없음',
    },
    {
      signalId: 'stock_link',
      label: '재고 연결',
      objectFieldKeys: ['stockLink', 'stock_link'],
      emptyDisplayDefault: '미연결',
    },
    {
      signalId: 'picking_availability',
      label: '피킹 가능',
      objectFieldKeys: ['pickingAvailable', 'picking_available'],
      emptyDisplayDefault: '불가',
    },
  ],
  item_sellable: [
    {
      signalId: 'stock_link',
      label: '재고 연결',
      objectFieldKeys: ['stockLink', 'stock_link'],
      emptyDisplayDefault: '미연결',
    },
    {
      signalId: 'reserved_quantity',
      label: '예약 수량',
      objectFieldKeys: ['reservedQuantity', 'reserved_quantity'],
      emptyDisplayDefault: '0',
    },
    {
      signalId: 'picking_availability',
      label: '피킹 가능',
      objectFieldKeys: ['pickingAvailable', 'picking_available'],
      emptyDisplayDefault: '불가',
    },
    {
      signalId: 'catalog_status',
      label: '카탈로그 상태',
      objectFieldKeys: ['catalogStatus', 'catalog_status'],
      emptyDisplayDefault: '미지정',
    },
  ],
  service_item: [
    {
      signalId: 'service_status',
      label: '서비스 상태',
      objectFieldKeys: ['serviceStatus', 'service_status', 'status'],
      emptyDisplayDefault: '미지정',
    },
    {
      signalId: 'delivery_window',
      label: '납기 창',
      objectFieldKeys: ['deliveryWindow', 'delivery_window', 'deliveryDate'],
      emptyDisplayDefault: '미설정',
    },
    {
      signalId: 'linked_contract',
      label: '연결 계약',
      objectFieldKeys: ['linkedContract', 'linked_contract', 'contractId'],
      emptyDisplayDefault: '없음',
    },
    {
      signalId: 'catalog_status',
      label: '카탈로그 상태',
      objectFieldKeys: ['catalogStatus', 'catalog_status'],
      emptyDisplayDefault: '미지정',
    },
  ],
  manufacturing_bom: [
    {
      signalId: 'bom_version',
      label: 'BOM 버전',
      objectFieldKeys: ['bomVersion', 'bom_version', 'version'],
      emptyDisplayDefault: '미지정',
    },
    {
      signalId: 'component_count',
      label: '구성품 수',
      objectFieldKeys: ['componentCount', 'component_count'],
      emptyDisplayDefault: '0',
    },
    {
      signalId: 'revision_status',
      label: '개정 상태',
      objectFieldKeys: ['revisionStatus', 'revision_status'],
      emptyDisplayDefault: '미지정',
    },
    {
      signalId: 'approval_flags',
      label: '승인 플래그',
      objectFieldKeys: ['approvalFlags', 'approval_flags'],
      emptyDisplayDefault: '없음',
    },
  ],
  manufacturing_production_order: [
    {
      signalId: 'production_status',
      label: '생산 상태',
      objectFieldKeys: ['productionStatus', 'production_status', 'orderStatus', 'order_status'],
      emptyDisplayDefault: '미지정',
    },
    {
      signalId: 'bom_link',
      label: 'BOM 연결',
      objectFieldKeys: ['bomLink', 'bom_link', 'bomId', 'linkedBomId'],
      emptyDisplayDefault: '미연결',
    },
    {
      signalId: 'quality_hold',
      label: '품질 홀드',
      objectFieldKeys: ['qualityHold', 'quality_hold'],
      emptyDisplayDefault: '해제',
    },
    {
      signalId: 'schedule_variance',
      label: '일정 편차',
      objectFieldKeys: ['scheduleVariance', 'schedule_variance'],
      emptyDisplayDefault: '없음',
    },
  ],
  entity: [
    {
      signalId: 'relation_count',
      label: '연결 관계',
      objectFieldKeys: ['relationCount', 'relation_count'],
      emptyDisplayDefault: '0',
    },
    {
      signalId: 'permission_status',
      label: '권한 상태',
      objectFieldKeys: ['permissionStatus', 'permission_status'],
      emptyDisplayDefault: '미지정',
    },
    {
      signalId: 'linked_documents',
      label: '연결 문서',
      objectFieldKeys: ['linkedDocuments', 'linked_documents'],
      emptyDisplayDefault: '없음',
    },
    {
      signalId: 'last_activity',
      label: '최근 활동',
      objectFieldKeys: ['lastActivityAt', 'last_activity_at'],
      emptyDisplayDefault: '없음',
    },
  ],
  entity_supplier: [
    {
      signalId: 'relation_count',
      label: '연결 관계',
      objectFieldKeys: ['relationCount', 'relation_count'],
      emptyDisplayDefault: '0',
    },
    {
      signalId: 'supplier_rating',
      label: '공급사 등급',
      objectFieldKeys: ['supplierRating', 'supplier_rating'],
      emptyDisplayDefault: '미지정',
    },
    {
      signalId: 'linked_documents',
      label: '연결 문서',
      objectFieldKeys: ['linkedDocuments', 'linked_documents'],
      emptyDisplayDefault: '없음',
    },
    {
      signalId: 'permission_status',
      label: '권한 상태',
      objectFieldKeys: ['permissionStatus', 'permission_status'],
      emptyDisplayDefault: '미지정',
    },
  ],
  knowledge_document: [
    {
      signalId: 'document_type',
      label: '문서 유형',
      objectFieldKeys: ['documentType', 'document_type'],
      emptyDisplayDefault: '미지정',
    },
    {
      signalId: 'linked_documents',
      label: '연결 문서',
      objectFieldKeys: ['linkedDocuments', 'linked_documents'],
      emptyDisplayDefault: '없음',
    },
    {
      signalId: 'last_activity',
      label: '최근 활동',
      objectFieldKeys: ['lastActivityAt', 'last_activity_at'],
      emptyDisplayDefault: '없음',
    },
    {
      signalId: 'validation_status',
      label: '검증 상태',
      objectFieldKeys: ['validationStatus', 'validation_status'],
      emptyDisplayDefault: '미검증',
    },
  ],
  order: [
    {
      signalId: 'order_status',
      label: '주문 상태',
      objectFieldKeys: ['orderStatus', 'order_status', 'status'],
      emptyDisplayDefault: '미지정',
    },
    {
      signalId: 'fulfillment_stage',
      label: '이행 단계',
      objectFieldKeys: ['fulfillmentStage', 'fulfillment_stage'],
      emptyDisplayDefault: '미지정',
    },
    {
      signalId: 'linked_customer',
      label: '연결 고객',
      objectFieldKeys: ['linkedCustomer', 'linked_customer', 'customerName'],
      emptyDisplayDefault: '없음',
    },
    {
      signalId: 'schedule_variance',
      label: '일정 편차',
      objectFieldKeys: ['scheduleVariance', 'schedule_variance'],
      emptyDisplayDefault: '없음',
    },
    {
      signalId: 'overdue_delivery',
      label: '납기 초과',
      objectFieldKeys: ['overdueDelivery', 'overdue_delivery', 'deliveryOverdueDays'],
      emptyDisplayDefault: '없음',
    },
    {
      signalId: 'delivery_evidence_missing',
      label: '납품 증빙 누락',
      objectFieldKeys: [
        'deliveryEvidenceMissing',
        'delivery_evidence_missing',
        'missingDeliveryEvidence',
        'missingDeliveryEvidenceCount',
      ],
      emptyDisplayDefault: '없음',
    },
    {
      signalId: 'production_queue',
      label: '생산 대기열',
      objectFieldKeys: ['productionQueue', 'production_queue'],
      emptyDisplayDefault: '없음',
    },
  ],
  inventory_shortage: [
    {
      signalId: 'shortage_quantity',
      label: '부족 수량',
      objectFieldKeys: ['shortageQuantity', 'shortage_quantity', 'shortageQty'],
      emptyDisplayDefault: '0',
    },
    {
      signalId: 'replenishment_status',
      label: '보충 상태',
      objectFieldKeys: ['replenishmentStatus', 'replenishment_status'],
      emptyDisplayDefault: '미지정',
    },
    {
      signalId: 'stock_link',
      label: '재고 연결',
      objectFieldKeys: ['stockLink', 'stock_link'],
      emptyDisplayDefault: '미연결',
    },
    {
      signalId: 'picking_availability',
      label: '피킹 가능',
      objectFieldKeys: ['pickingAvailable', 'picking_available'],
      emptyDisplayDefault: '불가',
    },
  ],
  task_execution: [
    {
      signalId: 'task_status',
      label: '업무 상태',
      objectFieldKeys: ['taskStatus', 'task_status', 'assignmentStatus', 'status'],
      emptyDisplayDefault: '미지정',
    },
    {
      signalId: 'assignee_link',
      label: '담당자',
      objectFieldKeys: ['assignee', 'assigneeName', 'assignee_name', 'assigneeId'],
      emptyDisplayDefault: '미배정',
    },
    {
      signalId: 'due_date',
      label: '마감일',
      objectFieldKeys: ['dueDate', 'due_date', 'deadline'],
      emptyDisplayDefault: '미지정',
    },
    {
      signalId: 'workflow_stage',
      label: '워크플로우 단계',
      objectFieldKeys: ['workflowStage', 'workflow_stage', 'flowStage', 'flow_stage'],
      emptyDisplayDefault: '미지정',
    },
    {
      signalId: 'missing_evidence_tasks',
      label: '증빙 누락 작업',
      objectFieldKeys: [
        'missingEvidenceTasks',
        'missing_evidence_tasks',
        'missingEvidenceTaskCount',
      ],
      emptyDisplayDefault: '0',
    },
    {
      signalId: 'overdue_assignments',
      label: '기한 초과 배정',
      objectFieldKeys: ['overdueAssignments', 'overdue_assignments'],
      emptyDisplayDefault: '0',
    },
  ],
  reference: [
    {
      signalId: 'reference_type',
      label: '참조 유형',
      objectFieldKeys: ['referenceType', 'reference_type', 'businessDomain', 'objectType'],
      emptyDisplayDefault: '미지정',
    },
    {
      signalId: 'linked_records',
      label: '연결 레코드',
      objectFieldKeys: ['linkedRecords', 'linked_records', 'relationCount', 'relation_count'],
      emptyDisplayDefault: '없음',
    },
    {
      signalId: 'last_activity',
      label: '최근 활동',
      objectFieldKeys: ['lastActivityAt', 'last_activity_at'],
      emptyDisplayDefault: '없음',
    },
    {
      signalId: 'validation_status',
      label: '검증 상태',
      objectFieldKeys: ['validationStatus', 'validation_status'],
      emptyDisplayDefault: '미검증',
    },
  ],
};

/** Layer-2 pilot domains for gate + API projection. */
export const INSPECTOR_SIGNAL_PILOT_DOMAINS = {
  entity_customer: ['customer', 'counterparty'] as const,
  entity_supplier: ['supplier'] as const,
  finance_receivables: ['finance_state', 'collection', 'ar_collection'] as const,
  finance_collection: ['collection', 'ar_collection'] as const,
  knowledge_document: [
    'document',
    'document_report',
    'document_policy',
    'knowledge_report',
  ] as const,
  warehouse: ['warehouse_picking_list', 'sellable_item', 'inventory_shortage'] as const,
  manufacturing_order: ['production_order', 'order', 'sales_order'] as const,
  inventory_shortage: ['inventory_shortage'] as const,
  order: ['order', 'sales_order'] as const,
  task_execution: ['task_execution', 'assignment_execution'] as const,
  reference: ['knowledge_report', 'flow_definition'] as const,
} as const;

export type InspectorSignalPilotFixtureKey = keyof typeof INSPECTOR_SIGNAL_PILOT_DOMAINS;

/** Minimal pilot fixtures — gate asserts ≥1 visible signal with populated value. */
export const INSPECTOR_SIGNAL_PILOT_FIXTURES: Readonly<
  Record<InspectorSignalPilotFixtureKey, Record<string, unknown>>
> = {
  entity_customer: {
    id: 'cust-pilot-1',
    name: 'Pilot Customer',
    businessDomain: 'customer',
    arAgingDays: 18,
    creditHold: false,
    relationCount: 2,
  },
  finance_receivables: {
    id: 'fin-pilot-1',
    name: 'Pilot Receivable',
    businessDomain: 'finance_state',
    overdueAmount: 120_000,
    arAgingDays: 32,
    creditHold: true,
    voucherStatus: 'posted',
  },
  finance_collection: {
    id: 'coll-pilot-1',
    name: 'Pilot Collection',
    businessDomain: 'collection',
    arAgingDays: 45,
    creditHold: false,
    overdueAmount: 85_000,
    paymentBehavior: 'late',
  },
  entity_supplier: {
    id: 'sup-pilot-1',
    name: 'Pilot Supplier',
    businessDomain: 'supplier',
    relationCount: 3,
    supplierRating: 'A',
    linkedDocuments: 2,
    permissionStatus: 'active',
  },
  knowledge_document: {
    id: 'doc-pilot-1',
    name: 'Pilot Document',
    businessDomain: 'document_report',
    documentType: 'policy',
    linkedDocuments: 1,
    lastActivityAt: '2026-06-27T12:00:00.000Z',
    validationStatus: 'valid',
  },
  warehouse: {
    id: 'wh-pilot-1',
    name: 'Pilot Pick',
    businessDomain: 'warehouse_picking_list',
    pickingStatus: 'pending',
    dispatchQueue: 'Q-01',
    stockLink: 'WH-A1',
    pickingAvailable: true,
  },
  manufacturing_order: {
    id: 'mfg-pilot-1',
    name: 'Pilot PO',
    businessDomain: 'production_order',
    productionStatus: 'released',
    bomLink: 'BOM-42',
    qualityHold: false,
    scheduleVariance: '+2d',
  },
  inventory_shortage: {
    id: 'inv-pilot-1',
    name: 'Pilot Shortage',
    businessDomain: 'inventory_shortage',
    shortageQuantity: 12,
    replenishmentStatus: 'pending',
    stockLink: 'WH-B2',
    pickingAvailable: false,
  },
  order: {
    id: 'ord-pilot-1',
    name: 'Pilot Order',
    businessDomain: 'order',
    orderStatus: 'confirmed',
    fulfillmentStage: 'picking',
    linkedCustomer: 'ACME',
    scheduleVariance: '+1d',
    overdueDelivery: true,
    deliveryEvidenceMissing: true,
    productionQueue: 'MFG-Q1',
  },
  task_execution: {
    id: 'task-pilot-1',
    name: 'Pilot Task',
    businessDomain: 'task_execution',
    taskStatus: 'in_progress',
    assigneeName: 'Ops Lead',
    dueDate: '2026-07-15',
    workflowStage: 'execution',
    missingEvidenceTasks: 2,
    overdueAssignments: 1,
  },
  reference: {
    id: 'ref-pilot-1',
    name: 'Pilot Reference',
    businessDomain: 'flow_definition',
    referenceType: 'flow',
    linkedRecords: 4,
    lastActivityAt: '2026-06-27T10:00:00.000Z',
    validationStatus: 'valid',
  },
};

function toCamelCase(key: string): string {
  return key.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
}

function readCustomFieldValue(
  customFields: unknown,
  key: string
): { present: boolean; value: unknown } {
  if (!Array.isArray(customFields)) {
    return { present: false, value: undefined };
  }
  for (const field of customFields) {
    if (!field || typeof field !== 'object' || !('key' in field)) {
      continue;
    }
    const fieldKey = String((field as { key: unknown }).key);
    if (fieldKey === key || fieldKey === toCamelCase(key) || toCamelCase(fieldKey) === key) {
      return { present: true, value: (field as { value?: unknown }).value };
    }
  }
  return { present: false, value: undefined };
}

function readFieldValues(record: Record<string, unknown>, key: string): unknown {
  const fieldValues = record.fieldValues;
  if (!fieldValues || typeof fieldValues !== 'object' || Array.isArray(fieldValues)) {
    return undefined;
  }
  const bag = fieldValues as Record<string, unknown>;
  if (key in bag) {
    return bag[key];
  }
  const camelKey = toCamelCase(key);
  return bag[camelKey];
}

export function readInspectorSignalObjectField(
  object: Record<string, unknown>,
  keys: string | readonly string[]
): unknown {
  const keyList = typeof keys === 'string' ? [keys] : keys;
  for (const key of keyList) {
    if (key in object) {
      return object[key];
    }
    const camelKey = toCamelCase(key);
    if (camelKey in object) {
      return object[camelKey];
    }
    const custom = readCustomFieldValue(object.customFields, key);
    if (custom.present) {
      return custom.value;
    }
    const fromFieldValues = readFieldValues(object, key);
    if (fromFieldValues !== undefined) {
      return fromFieldValues;
    }
  }
  return undefined;
}

export function isInspectorSignalFieldPresent(
  object: Record<string, unknown>,
  keys: string | readonly string[]
): boolean {
  const keyList = typeof keys === 'string' ? [keys] : keys;
  for (const key of keyList) {
    if (key in object || toCamelCase(key) in object) {
      return true;
    }
    if (readCustomFieldValue(object.customFields, key).present) {
      return true;
    }
    if (readFieldValues(object, key) !== undefined) {
      return true;
    }
  }
  return false;
}

export function formatInspectorSignalRawValue(value: unknown): string | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (typeof value === 'boolean') {
    return value ? '예' : '아니오';
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  return String(value);
}

export function resolveInspectorSignalDisplayValue(input: {
  object: Record<string, unknown>;
  objectFieldKeys: readonly string[];
  emptyDisplayDefault: string;
}): string | undefined {
  const raw = readInspectorSignalObjectField(input.object, input.objectFieldKeys);
  const formatted = formatInspectorSignalRawValue(raw);
  if (formatted !== undefined) {
    return formatted;
  }
  if (isInspectorSignalFieldPresent(input.object, input.objectFieldKeys)) {
    return input.emptyDisplayDefault;
  }
  return undefined;
}

export function resolveInspectorSignalSpecForId(
  pilotKey: DomainCockpitPilotKey,
  signalId: string
): InspectorSignalFieldSpec | undefined {
  return INSPECTOR_SIGNAL_FIELD_CONTRACT[pilotKey]?.find((spec) => spec.signalId === signalId);
}

export function flattenEntityCustomFieldsForInspectorSignals(
  entity: UniversalBaseEntity
): Record<string, unknown> {
  const record = { ...(entity as Record<string, unknown>) };
  const customFields = record.customFields;
  if (!Array.isArray(customFields)) {
    return record;
  }

  for (const field of customFields) {
    if (!field || typeof field !== 'object' || !('key' in field)) {
      continue;
    }
    const key = String((field as { key: unknown }).key);
    const camelKey = toCamelCase(key);
    const value = (field as { value?: unknown }).value;
    if (!(camelKey in record) && !(key in record)) {
      record[camelKey] = value;
    }
  }

  const fieldValues = record.fieldValues;
  if (fieldValues && typeof fieldValues === 'object' && !Array.isArray(fieldValues)) {
    for (const [key, value] of Object.entries(fieldValues as Record<string, unknown>)) {
      const camelKey = toCamelCase(key);
      if (!(camelKey in record) && !(key in record)) {
        record[camelKey] = value;
      }
    }
  }

  return record;
}

export function resolveInspectorSignalPilotKey(
  businessDomain: string
): DomainCockpitPilotKey | undefined {
  return resolveDomainCockpitPilotKey(businessDomain);
}

export function listInspectorSignalContractForDomain(
  businessDomain: string
): readonly InspectorSignalFieldSpec[] {
  const pilotKey = resolveInspectorSignalPilotKey(businessDomain);
  if (!pilotKey) {
    return [];
  }
  return INSPECTOR_SIGNAL_FIELD_CONTRACT[pilotKey] ?? [];
}
